import * as React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';

import ResultCULGridListService from '../../logic/api/ResultCULGridListService';
import ResultGridListService from "../../logic/api/ResultGridListService";
import { CellSubCareerItem } from '../../logic/api/SkillMapCellService';

/** キャリア実績取得用 */
const resultGridService = new ResultGridListService();
/** カリキュラム実績取得用 */
const resultCULGridListService = new ResultCULGridListService();

/**
 * #19063: 置き換え対象ステータスを定義(カリキュラム)
 */
export const curriculaStatusReplacementDefs = {
    "実施中": "受講中",
} as const;

/**
 * #19063: 置き換え対象に含まれるならtrue (type guards) (カリキュラム)
 */
export const isCurriculaStatusToBeReplaced = (() => {
    const keys = Object.keys(curriculaStatusReplacementDefs);
    return function (arg: unknown): arg is keyof typeof curriculaStatusReplacementDefs {
        if (typeof arg !== 'string') return false;
        return keys.includes(arg);
    }
})();

/**
 * #19063: 一部ラベルを置き換え (カリキュラム)
 */
export function replaceCurriculaStatus(status: string): string;
export function replaceCurriculaStatus(status: string | undefined): string | undefined;
export function replaceCurriculaStatus(status: string | undefined) {
    // 差し替え対象のステータスなら
    return isCurriculaStatusToBeReplaced(status)
        // オブジェクト内を差し替えておく
        ? curriculaStatusReplacementDefs[status]
        // それ以外はそのまま
        : status
}

/**
 * #19063: 置き換え対象ステータスを定義 (所属キャリア)
 */
export const subcareersStatusReplacementDefs = {
    "実施中": "学習中",
} as const;

/**
 * #19063: 置き換え対象に含まれるならtrue (type guards) (所属キャリア)
 */
export const isSubcareersStatusToBeReplaced = (() => {
    const keys = Object.keys(subcareersStatusReplacementDefs);
    return function (arg: unknown): arg is keyof typeof subcareersStatusReplacementDefs {
        if (typeof arg !== 'string') return false;
        return keys.includes(arg);
    }
})();

/**
 * #19063: 一部ラベルを置き換え (所属キャリア)
 */
export function replaceSubcareersStatus(status: string): string;
export function replaceSubcareersStatus(status: string | undefined): string | undefined;
export function replaceSubcareersStatus(status: string | undefined) {
    // 差し替え対象のステータスなら
    return isSubcareersStatusToBeReplaced(status)
        // オブジェクト内を差し替えておく
        ? subcareersStatusReplacementDefs[status]
        // それ以外はそのまま
        : status;
}

/**
 * 内部用 - コンテキスト保存用構造: map[careerId][classId] = status
 */
type ResultStatusMap = Readonly<{
    [CareerId in string]: {
        [ClassId in string]: string
    }
}>;

/**
 * 内部用 - React Contextオブジェクト
 */
const ResultStatusContext = React.createContext<ResultStatusMap>({});

/**
 * #19063: 実績ステータス格納用コンテキストプロバイダ
 * 
 * このコンポーネントの初期化時に実績情報を取得してきてContextに保持する
 * useSubcareersStatusConversion および useCarriculaStatusConversion 使うコンポーネントをこれで括る
 */
export function ResultStatusProvider({ children }: React.PropsWithChildren) {
    const [value, setValue] = useState<ResultStatusMap>({});
    useEffect(() => {
        let mounted = true;

        const initialize = async () => {
            try {
                const [{ resultGridList: careers }, { resultCULGridList: curricula }] =
                    await Promise.all([
                        resultGridService.fetchAll(),
                        resultCULGridListService.fetchAll(),
                    ] as const);
                const result = [
                    ...careers.map(({ careerId, classId, status }) => ({ careerId, classId, status })),
                    ...curricula.map(({ careerId, status }) => ({ careerId, classId: "", status })),
                ];

                // 整形
                const map = result.reduce((ret, { careerId, classId, status }) => ({
                    ...ret,
                    [careerId]: {
                        ...(ret[careerId] ?? {}),
                        [classId]: status,
                    },
                }), {} as ResultStatusMap);

                if (mounted) {
                    setValue(map);
                }
            } catch (e: unknown) {
                console.error(e);
            }
        }
        initialize();

        return () => {
            mounted = false
        }
    }, [setValue]);

    return <ResultStatusContext.Provider value={value}>
        {children}
    </ResultStatusContext.Provider>
}

/**
 * #19063: useSubcareersStatusConversion および useCarriculaStatusConversion の引数型
 * 
 * CellSubCareerItemをそのまま渡せる
 */
export type ResultCareerKeys = Pick<CellSubCareerItem, 'careerId' | 'classId' | 'status'>;

/**
 * 内部hooks: 保存した実績ステータスラベルを引き出す / 存在しなければundefined
 */
const useResultStatus = ({ careerId, classId }: ResultCareerKeys) => {
    const map = useContext(ResultStatusContext);
    const status: string | undefined = map?.[careerId]?.[classId] ?? undefined;
    return status;
}

/**
 * #19063: Subcareer用ステータス文字列変換hooks
 * 
 * これを使うコンポーネントは<ResultStatusProvider>で括る
 */
export const useSubcareersStatusConversion = ({
    careerId,
    classId,
    status,
}: ResultCareerKeys) => {
    // 実績のステータスを取得
    const fromResult = useResultStatus({ careerId, classId, status, });
    // 実績に無い場合は元のステータスを採用
    const replacement = useMemo(() => fromResult ?? status, [status, fromResult]);
    // ルールに従って置換
    const replaced = useMemo(() => replaceSubcareersStatus(replacement), [replacement]);
    return replaced;
}

/**
 * #19063: Curriculum用ステータス文字列変換hooks
 * 
 * これを使うコンポーネントは<ResultStatusProvider>で括る
 */
export const useCarriculaStatusConversion = ({
    careerId,
    // classId, // そもそもnullで来るので型が壊れている。空文字列固定で処理
    status,
}: ResultCareerKeys) => {
    // 実績のステータスを取得
    const fromResult = useResultStatus({ careerId, classId: "", status });
    // 実績に無い場合は元のステータスを採用
    const replacement = useMemo(() => fromResult ?? status, [status, fromResult]);
    // ルールに従って置換
    const replaced = useMemo(() => replaceCurriculaStatus(replacement), [replacement]);
    return replaced;
}
